import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronRight, SearchX } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import {
  GatewayActions,
  GatewayBrand,
  GatewayContent,
  GatewayShell,
  GatewaySpinner,
} from '@/_shared/components/GatewayShell';
import { LoginRedirect } from '@/_shared/components/LoginRedirect';
import { RudderstackBrandLogo } from '@/_shared/components/RudderstackBrandLogo';
import { TruncateWithTooltip } from '@/_shared/components/TruncateWithTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/_shared/components/ui/empty';
import { Input } from '@/_shared/components/ui/input';
import { useAuth } from '@/_shared/hooks/useAuth';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { useRudderstackReauth } from '@/_shared/hooks/useRudderstackReauth';
import { orpc } from '@/_shared/lib/orpc';
import type { client } from '@/_shared/lib/rpc';
import { cn } from '@/_shared/lib/utils';

import { PendingInvites } from '../components/pendingInvites/PendingInvites';
import {
  CreatedByLine,
  MEMBERSHIP_SUSPENDED_MESSAGE,
  RegionBadge,
  WorkspaceUrlField,
} from '../components/workspacePicker/PickerParts';
import { SiblingWorkspacePicker } from '../components/workspacePicker/SiblingWorkspacePicker';
import { useWorkspaceSlug } from '../hooks/useWorkspaceSlug';

/** One picker row, derived from the listing procedure's output. */
type PickerWorkspace = Awaited<ReturnType<typeof client.workspace.listRudderstackWorkspaces>>['workspaces'][number];

/** The Lookout workspace a row offers to join. */
type JoinTarget = NonNullable<PickerWorkspace['existingWorkspace']>;

const RUDDERSTACK_WORKSPACES_KEY = orpc.workspace.listRudderstackWorkspaces.key();
const SIBLING_PICKER_KEY = orpc.workspace.listSiblingPicker.key();
const WORKSPACE_DISABLED_MESSAGE =
  'This workspace is disabled or deleted in RudderStack, so Lookout cannot use it. Ask a RudderStack admin if you need it.';
const PAT_LIMIT_MESSAGE =
  'This RudderStack workspace has too many access tokens. Delete unused tokens in RudderStack, then try again.';
const RATE_LIMITED_MESSAGE = 'RudderStack is busy right now. Wait a minute, then try again.';
const PAT_REJECTED_MESSAGE = 'RudderStack refused to connect this workspace. Contact customer support.';
const ORG_ADMIN_ASK_ADMIN_MESSAGE =
  'Only admins of the organization that owns this workspace can create a Lookout workspace for it. Ask an admin to create it, then join it here.';
const LINK_UNVERIFIED_MESSAGE = "Couldn't join. This workspace is not properly configured. Contact customer support.";
const LINKED_UNVERIFIED_MESSAGE =
  'This RudderStack workspace already has a Lookout workspace, but it is not properly configured. Contact customer support.';
const LINKED_UNCHECKED_MESSAGE =
  'This RudderStack workspace already has a Lookout workspace, but Lookout could not check it right now. Refresh the list to try again.';

/**
 * Why Create is disabled on this row, or null when it is enabled. Suspension wins over the other rules.
 * Block only on an explicit true/false, like the server: a missing field (version skew) stays creatable.
 */
function createBlockedReason(ws: PickerWorkspace): string | null {
  if (ws.suspended === true) return MEMBERSHIP_SUSPENDED_MESSAGE;
  if (ws.linked === true) return ws.linkUnverified === true ? LINKED_UNVERIFIED_MESSAGE : LINKED_UNCHECKED_MESSAGE;
  if (ws.canCreate === false) return ORG_ADMIN_ASK_ADMIN_MESSAGE;
  return null;
}

/** Marks every query old, but waits only for the session that the next page reads, not for this picker's list. */
async function refreshSessionBeforeLeaving(queryClient: QueryClient): Promise<void> {
  void queryClient.invalidateQueries({ refetchType: 'none' });
  await queryClient.invalidateQueries({ queryKey: orpc.auth.me.key() });
}

/** True for the typed upstream failures, which each action reports with its own plain message. */
function isUpstreamFailure(message: string): boolean {
  return message.includes('rudderstack_unavailable') || message.includes('rudderstack_forbidden');
}

export function NewWorkspacePicker(): React.ReactElement {
  usePageTitle('Pick a workspace');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, demoWorkspace, loading: authLoading, signOutAndRedirect } = useAuth();

  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const slugField = useWorkspaceSlug();

  const listQuery = useQuery(orpc.workspace.listRudderstackWorkspaces.queryOptions());
  // Each row's focus targets after a create refusal: the Create control's focusable wrapper, or the
  // Join button when the row has become a join card.
  const createTriggerRefs = useRef(new Map<string, HTMLSpanElement>());
  const joinTriggerRefs = useRef(new Map<string, HTMLButtonElement>());
  // A new object for each refusal, so the effect runs once for each.
  const [refocusRow, setRefocusRow] = useState<{ id: string } | null>(null);
  // Takes focus when a refusal removes the row the user acted on, so focus never drops to the page body.
  const listRef = useRef<HTMLDivElement>(null);
  const [focusAfterRefusal, setFocusAfterRefusal] = useState<{ id: string; index: number } | null>(null);
  // The request the effect already served, so a later join settling does not move focus again.
  const handledRefusalRef = useRef<{ id: string; index: number } | null>(null);

  /** The refused row's position among the rendered rows, read before the refetch removes it. */
  const renderedIndexOf = (rsWorkspaceId: string): number =>
    Array.from(document.querySelectorAll<HTMLElement>('li[data-rs-workspace-id]')).findIndex(
      (li) => li.dataset['rsWorkspaceId'] === rsWorkspaceId,
    );

  // After a create refusal and its list refetch, move focus once onto the refused row's control.
  useEffect(() => {
    if (refocusRow === null) return;
    const wrapper = createTriggerRefs.current.get(refocusRow.id);
    // The wrapper takes focus only while it shows a reason. An enabled Create takes focus itself.
    const createTarget = wrapper?.tabIndex === 0 ? wrapper : wrapper?.querySelector('button');
    (joinTriggerRefs.current.get(refocusRow.id) ?? createTarget)?.focus();
  }, [refocusRow]);

  // Post-disconnect / refresh-expiry: a RudderStack identity but no tokens → bounce through re-auth.
  // That's a real navigation side effect, so it stays here; the no-RudderStack-at-all case is derived
  // during render (see `noRudderstackAccount`) rather than routed.
  const reauth = useRudderstackReauth();
  const reauthStartedRef = useRef(false);
  useEffect(() => {
    const data = listQuery.data;
    if (!data || data.hasTokens || !data.hasRudderstackIdentity) return;
    // The provider probe decides the path; the native one signs out, so it must run once.
    if (reauth.isLoading || reauthStartedRef.current) return;
    reauthStartedRef.current = true;
    // Cognito session may still be alive → silent bounce; else one tap to sign in.
    reauth.start();
  }, [listQuery.data, reauth]);

  const { prefill } = slugField;
  const openImport = useCallback(
    (ws: PickerWorkspace) => {
      setExpandedId(ws.id);
      void prefill(ws.name);
    },
    [prefill],
  );

  const createMutation = useMutation(
    orpc.workspace.createFromRudderstack.mutationOptions({
      onSuccess: async (data) => {
        await refreshSessionBeforeLeaving(queryClient);
        void navigate(`/w/${data.workspace.id}/settings`, { replace: true });
      },
      onError: async (err: Error, vars) => {
        const message = err.message;
        if (message.includes('rudderstack_reauth_required')) {
          reauth.start();
          return;
        }
        if (isUpstreamFailure(message) || message.includes('rudderstack_pat_forbidden')) {
          toast.error('Unable to create workspace. Try again.');
          return;
        }
        // These errors come after the server re-fetched /getUser and re-primed the cache, so a list
        // refetch shows the current state: the row gone, or its Create disabled. Collapse only the row
        // that failed — the user may have opened another one while the request was pending.
        const refusedRow = vars.rudderstackWorkspaceId;
        if (message.includes('workspace_not_in_user_list')) {
          setExpandedId((id) => (id === refusedRow ? null : id));
          await queryClient.invalidateQueries({ queryKey: RUDDERSTACK_WORKSPACES_KEY });
          toast.error('This workspace is no longer in your RudderStack account.');
          return;
        }
        if (message.includes('rudderstack_membership_suspended')) {
          setExpandedId((id) => (id === refusedRow ? null : id));
          await queryClient.invalidateQueries({ queryKey: RUDDERSTACK_WORKSPACES_KEY });
          toast.error(MEMBERSHIP_SUSPENDED_MESSAGE);
          return;
        }
        if (message.includes('rudderstack_workspace_disabled')) {
          const indexBefore = renderedIndexOf(refusedRow);
          setExpandedId((id) => (id === refusedRow ? null : id));
          await queryClient.invalidateQueries({ queryKey: RUDDERSTACK_WORKSPACES_KEY });
          setFocusAfterRefusal({ id: refusedRow, index: indexBefore });
          toast.error(WORKSPACE_DISABLED_MESSAGE);
          return;
        }
        if (message.includes('rudderstack_pat_limit_reached')) {
          toast.error(PAT_LIMIT_MESSAGE);
          return;
        }
        if (message.includes('rudderstack_rate_limited')) {
          toast.error(RATE_LIMITED_MESSAGE);
          return;
        }
        if (message.includes('rudderstack_pat_rejected')) {
          toast.error(PAT_REJECTED_MESSAGE);
          return;
        }
        if (message.includes('rudderstack_org_admin_required')) {
          setExpandedId((id) => (id === refusedRow ? null : id));
          await queryClient.invalidateQueries({ queryKey: RUDDERSTACK_WORKSPACES_KEY });
          setRefocusRow({ id: refusedRow });
          toast.error(ORG_ADMIN_ASK_ADMIN_MESSAGE);
          return;
        }
        if (message.includes('rudderstack_workspace_already_linked')) {
          // A teammate created one since the list loaded. The refetch shows it to join, or Create disabled.
          setExpandedId((id) => (id === refusedRow ? null : id));
          await queryClient.invalidateQueries({ queryKey: RUDDERSTACK_WORKSPACES_KEY });
          setRefocusRow({ id: refusedRow });
          toast.error('This RudderStack workspace already has a Lookout workspace.');
          return;
        }
        if (message.includes('workspace_creation_requires_rudderstack')) {
          // Shouldn't happen here (reaching the picker implies a RudderStack login), but surface it
          // clearly rather than as a raw error string if the policy ever rejects this account.
          toast.error('Creating a workspace requires signing in with RudderStack.');
          return;
        }
        toast.error(message || 'Could not create the workspace. Please try again.');
      },
    }),
  );

  const joinMutation = useMutation(
    orpc.workspace.joinRudderstackWorkspace.mutationOptions({
      onSuccess: async (data) => {
        await refreshSessionBeforeLeaving(queryClient);
        void navigate(`/w/${data.workspace.id}`, { replace: true });
      },
      onError: async (err: Error, vars) => {
        const message = err.message;
        if (message.includes('rudderstack_reauth_required')) {
          reauth.start();
          return;
        }
        if (isUpstreamFailure(message)) {
          toast.error('Unable to join workspace. Try again.');
          return;
        }
        if (message.includes('rudderstack_membership_suspended')) {
          await queryClient.invalidateQueries({ queryKey: RUDDERSTACK_WORKSPACES_KEY });
          toast.error(MEMBERSHIP_SUSPENDED_MESSAGE);
          return;
        }
        if (message.includes('rudderstack_workspace_disabled')) {
          const indexBefore = renderedIndexOf(vars.rudderstackWorkspaceId);
          await queryClient.invalidateQueries({ queryKey: RUDDERSTACK_WORKSPACES_KEY });
          setFocusAfterRefusal({ id: vars.rudderstackWorkspaceId, index: indexBefore });
          toast.error(WORKSPACE_DISABLED_MESSAGE);
          return;
        }
        if (message.includes('not_rudderstack_member')) {
          // No longer a co-member upstream — refetch so the stale Join nudge disappears.
          await queryClient.invalidateQueries({ queryKey: RUDDERSTACK_WORKSPACES_KEY });
          toast.error("You're no longer a member of this RudderStack workspace.");
          return;
        }
        if (message.includes('rudderstack_link_unverified')) {
          await queryClient.invalidateQueries({ queryKey: RUDDERSTACK_WORKSPACES_KEY });
          toast.error(LINK_UNVERIFIED_MESSAGE);
          return;
        }
        if (message.includes('no_existing_workspace')) {
          // The hint was stale (the workspace was deleted or its link no longer proves) — refetch, then word
          // the toast by what the row now offers: another linked workspace, or Create for this user or not.
          await queryClient.invalidateQueries({ queryKey: RUDDERSTACK_WORKSPACES_KEY });
          const row = queryClient
            .getQueryData(orpc.workspace.listRudderstackWorkspaces.queryKey())
            ?.workspaces.find((w) => w.id === vars.rudderstackWorkspaceId);
          toast.message(
            row?.linked === true
              ? 'That workspace is no longer available.'
              : row?.canCreate === false
                ? 'That workspace is no longer available. Ask an admin of your organization to create a new one.'
                : 'That workspace is no longer available — create a new one instead.',
          );
          return;
        }
        toast.error(message || 'Could not join the workspace. Please try again.');
      },
    }),
  );

  // After a disabled refusal and the list refetch: focus the refused row's action if it is still shown
  // (the server could not confirm the new status), else the row now at its position, else the list,
  // which holds the empty state. It waits for the join to settle, because a pending join disables
  // every Join button.
  useEffect(() => {
    if (focusAfterRefusal === null || joinMutation.isPending || handledRefusalRef.current === focusAfterRefusal) return;
    handledRefusalRef.current = focusAfterRefusal;
    const list = listRef.current;
    const rows = list ? Array.from(list.querySelectorAll<HTMLElement>('li[data-rs-workspace-id]')) : [];
    const row =
      rows.find((li) => li.dataset['rsWorkspaceId'] === focusAfterRefusal.id) ??
      rows[Math.min(Math.max(focusAfterRefusal.index, 0), rows.length - 1)];
    // A blocked action is wrapped in a focusable tooltip trigger; an enabled one is the button itself.
    (row?.querySelector<HTMLElement>('button:not([disabled]), [tabindex="0"]') ?? list)?.focus();
  }, [focusAfterRefusal, joinMutation.isPending]);

  const refreshMutation = useMutation(
    orpc.workspace.refreshRudderstackWorkspaces.mutationOptions({
      // The refresh returns the same rows as the list, so the picker shows them without a second call.
      onSuccess: async (data) => {
        const queryKey = orpc.workspace.listRudderstackWorkspaces.queryKey();
        // An older list request still running must not replace these rows when it finishes.
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData(queryKey, data);
        toast.success('Workspace list updated');
      },
      onError: (err: Error) =>
        toast.error(
          isUpstreamFailure(err.message)
            ? 'Unable to refresh the list. Try again.'
            : err.message || 'Could not refresh your workspace list.',
        ),
    }),
  );

  // With the Lookout flag on, the list is the sibling picker's (ACT2-1124); a refresh reads it again.
  const refreshSiblingsMutation = useMutation({
    mutationFn: () => queryClient.refetchQueries({ queryKey: SIBLING_PICKER_KEY }, { throwOnError: true }),
    onSuccess: () => toast.success('Workspace list updated'),
    onError: () => toast.error('Unable to refresh the list. Try again.'),
  });

  const leaveAfterCreate = useCallback(
    async (workspaceId: string) => {
      await refreshSessionBeforeLeaving(queryClient);
      void navigate(`/w/${workspaceId}/settings`, { replace: true });
    },
    [navigate, queryClient],
  );

  const leaveAfterJoin = useCallback(
    async (workspaceId: string) => {
      await refreshSessionBeforeLeaving(queryClient);
      void navigate(`/w/${workspaceId}`, { replace: true });
    },
    [navigate, queryClient],
  );

  // No auto-join, no invite link: reaching the demo workspace is always this
  // deliberate choice. `demoWorkspace` is already null server-side once the caller has joined, so a
  // second click here is unreachable rather than merely harmless.
  const joinDemoMutation = useMutation(
    orpc.workspace.joinDemo.mutationOptions({
      onSuccess: async (data) => {
        await refreshSessionBeforeLeaving(queryClient);
        // Hardcoded prefix: this is a pre-workspace auth view, so `wp()` (which needs an active
        // workspace) isn't available here — same exception the create/join mutations above rely on.
        void navigate(`/w/${data.slug}/`, { replace: true });
      },
      onError: (err: Error) => toast.error(err.message || 'Could not join the demo workspace. Please try again.'),
    }),
  );

  if (!authLoading && !user) {
    return <LoginRedirect />;
  }

  // Rendered wherever a non-member can land on this screen. No auto-join, no invite link — this
  // button is the only door into the demo workspace, and `demoWorkspace` is server-computed to be
  // null once the caller has already joined (or none is configured), so the option disappears
  // rather than becoming a no-op.
  const demoWorkspaceOption = demoWorkspace && (
    <div className="mt-6 rounded-lg border border-dashed border-border p-3 text-left">
      <p className="text-sm font-medium text-foreground">Just exploring?</p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Jump into the {demoWorkspace.name} demo workspace — no setup required.
      </p>
      <Button
        type="button"
        variant="secondary"
        size="md"
        className="mt-2 w-full"
        aria-label={`Join ${demoWorkspace.name}`}
        loading={joinDemoMutation.isPending}
        onClick={() => joinDemoMutation.mutate(undefined)}
      >
        Join demo workspace
      </Button>
    </div>
  );

  const data = listQuery.data;
  const showPicker = !!data?.hasTokens;
  const pickerReady = !listQuery.isError && !listQuery.isPending && showPicker;
  // ACT2-1124: with the Lookout flag on, the picker creates and joins sibling workspaces.
  const siblingAccess = data?.siblingAccess === true;
  const refreshing = siblingAccess ? refreshSiblingsMutation.isPending : refreshMutation.isPending;
  // No RudderStack connection at all (no tokens, no identity): manual creation is gone, so surface the
  // RudderStack requirement here. Derived from the query — no effect/state needed.
  const noRudderstackAccount = !!data && !data.hasTokens && !data.hasRudderstackIdentity;

  const workspaces: PickerWorkspace[] = data?.workspaces ?? [];
  // Hide RudderStack workspaces the current user has already imported: those are reachable from the
  // "Select a workspace" screen, so listing them here (with an Open button) is redundant and, per
  // review, confusing. This screen is only for linking a new workspace or joining a teammate's
  // (AI-875). `alreadyImportedAs` is therefore always null for every row rendered below.
  const linkable = workspaces.filter((w) => w.alreadyImportedAs === null);
  // The search box shows only above three rows. A refresh can drop the list below that, and a saved
  // query must not then hide rows behind "No matches" with no box to clear it.
  const activeSearch = linkable.length > 3 ? search.trim().toLowerCase() : '';
  const filtered = linkable.filter((w) => w.name.toLowerCase().includes(activeSearch));
  // Within the shown rows, club the teammate-joinable ones (AI-875) above the brand-new ones so each
  // reads as a coherent group rather than Join/Link interleaved alphabetically.
  const joinable = filtered.flatMap((ws) => (ws.existingWorkspace ? [{ ws, target: ws.existingWorkspace }] : []));
  const toLink = filtered.filter((w) => w.existingWorkspace === null);

  // Visible label stays a clean "Join"; the workspace name lives in aria-label so screen readers can tell
  // identical Join buttons apart. A join anywhere disables ALL join buttons (prevents a double-click race
  // where two rows navigate concurrently), but only the one being joined shows the spinner (AI-875).
  const renderJoinButton = (ws: PickerWorkspace, target: JoinTarget): React.ReactElement => (
    <ActionTooltip show={ws.suspended === true} message={MEMBERSHIP_SUSPENDED_MESSAGE}>
      <Button
        ref={(el: HTMLButtonElement | null) => {
          if (el) joinTriggerRefs.current.set(ws.id, el);
          else joinTriggerRefs.current.delete(ws.id);
        }}
        variant="primary"
        size="md"
        aria-label={`Join ${target.name}`}
        loading={joinMutation.isPending && joinMutation.variables?.rudderstackWorkspaceId === ws.id}
        disabled={joinMutation.isPending || ws.suspended === true}
        onClick={() => joinMutation.mutate({ rudderstackWorkspaceId: ws.id })}
      >
        Join
      </Button>
    </ActionTooltip>
  );

  // A teammate already linked this RudderStack workspace and the current user isn't in it yet (AI-875).
  // The card offers the oldest linked Lookout workspace whose link passes the check.
  const renderJoinCard = ({ ws, target }: { ws: PickerWorkspace; target: JoinTarget }): React.ReactElement => (
    <li key={ws.id} data-rs-workspace-id={ws.id} className="rounded-xl border border-border">
      {/* Same wrapping as the create card: the button drops to its own line only when the names need the space. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-3 px-4 py-3">
        <div className="flex max-w-full min-w-0 shrink-0 flex-col gap-1 text-left">
          <div className="flex min-w-0 items-center gap-2">
            <TruncateWithTooltip className="text-sm font-medium text-foreground">{ws.name}</TruncateWithTooltip>
            {ws.defaultRegion && <RegionBadge region={ws.defaultRegion} />}
          </div>
          <p className="flex min-w-0 items-baseline gap-1 text-xs text-muted-foreground">
            <span className="shrink-0">Linked as</span>
            <TruncateWithTooltip className="font-medium text-foreground">{target.name}</TruncateWithTooltip>
          </p>
          {/* A suspended member gets no owner details from the server, so the line is hidden. */}
          {ws.suspended !== true && <CreatedByLine target={target} />}
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">{renderJoinButton(ws, target)}</div>
      </div>
    </li>
  );

  const renderCreateCard = (ws: PickerWorkspace): React.ReactElement => {
    const blockedReason = createBlockedReason(ws);
    const expanded = expandedId === ws.id && blockedReason === null;
    return (
      <li key={ws.id} data-rs-workspace-id={ws.id} className="rounded-xl border border-border">
        {/* flex-wrap so the button only drops to a new line when the name column needs the space:
            the name column is shrink-0 (sized to its content, capped at the full row width), so a long
            name pushes the button to wrap — and only when the name alone exceeds a full line does the
            inner truncation kick in. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-3 px-4 py-3">
          <div className="flex max-w-full min-w-0 shrink-0 items-center gap-2 text-left">
            <TruncateWithTooltip className="text-sm font-medium text-foreground">{ws.name}</TruncateWithTooltip>
            {ws.defaultRegion && <RegionBadge region={ws.defaultRegion} />}
          </div>
          {/* ml-auto keeps the button right-aligned whether it sits inline with the name or wraps onto
              its own line. */}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <ActionTooltip
              show={blockedReason !== null}
              message={blockedReason ?? ORG_ADMIN_ASK_ADMIN_MESSAGE}
              keepMounted
              triggerRef={(el) => {
                if (el) createTriggerRefs.current.set(ws.id, el);
                else createTriggerRefs.current.delete(ws.id);
              }}
            >
              <Button
                variant="secondary"
                size="md"
                disabled={blockedReason !== null}
                aria-expanded={expanded}
                aria-controls={`import-panel-${ws.id}`}
                suffixIcon={<ChevronRight className={cn('transition-transform', expanded && 'rotate-90')} />}
                onClick={() => (expanded ? setExpandedId(null) : openImport(ws))}
              >
                Create
              </Button>
            </ActionTooltip>
          </div>
        </div>

        {expanded && (
          <div id={`import-panel-${ws.id}`} className="border-t border-border px-4 py-3 text-left">
            <WorkspaceUrlField id={`slug-${ws.id}`} field={slugField} />

            <Button
              type="button"
              variant="primary"
              size="lg"
              className="mt-3 w-full"
              disabled={slugField.status !== 'available' || createMutation.isPending}
              loading={createMutation.isPending}
              onClick={() => createMutation.mutate({ slug: slugField.slug, rudderstackWorkspaceId: ws.id })}
            >
              Create Workspace
            </Button>
          </div>
        )}
      </li>
    );
  };

  const pickerFooter = (
    <div className="mt-6 shrink-0 space-y-2 text-xs text-muted-foreground">
      <p>
        Don't see your workspace?{' '}
        <Button
          variant="text"
          className="text-xs text-primary underline-offset-4 hover:underline"
          disabled={refreshing}
          onClick={() => (siblingAccess ? refreshSiblingsMutation.mutate() : refreshMutation.mutate(undefined))}
        >
          {refreshing ? 'Refreshing…' : 'Refresh the list'}
        </Button>
      </p>
      <p>
        <Button
          variant="text"
          className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          onClick={() => void signOutAndRedirect()}
        >
          Sign in with a different account
        </Button>
      </p>
    </div>
  );

  if (noRudderstackAccount) {
    return (
      <GatewayShell className="max-w-md">
        <GatewayBrand />
        <GatewayContent
          title="RudderStack account required"
          description="Creating a workspace is only available when you sign in with RudderStack. You're currently signed in with a different account. Any workspaces you already belong to are unaffected — you can keep using them."
        />
        <GatewayActions>
          {/* While the probe loads, a disabled button stands in for either path. */}
          {reauth.native || reauth.isLoading ? (
            <Button
              variant="primary"
              size="lg"
              className="w-full gap-2"
              Icon={<RudderstackBrandLogo variant="compact" className="size-4 text-primary-foreground" />}
              loading={reauth.pending || reauth.isLoading}
              onClick={reauth.start}
            >
              Continue with RudderStack
            </Button>
          ) : (
            <Button variant="primary" size="lg" className="w-full gap-2" asChild>
              <a href={reauth.oauthUrl}>
                <RudderstackBrandLogo variant="compact" className="size-4 text-primary-foreground" />
                Continue with RudderStack
              </a>
            </Button>
          )}
          <Button
            variant="text"
            className="self-center text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            onClick={() => void signOutAndRedirect()}
          >
            Sign in with a different account
          </Button>
        </GatewayActions>
        {demoWorkspaceOption}
      </GatewayShell>
    );
  }

  return (
    <GatewayShell className="flex max-h-[calc(100vh-5rem)] max-w-md flex-col">
      <GatewayBrand />
      <GatewayContent
        title={siblingAccess ? 'Create or join a workspace' : 'Create your workspace'}
        description={
          siblingAccess
            ? 'Create a Lookout workspace in your RudderStack organization, or join one.'
            : 'Pick a RudderStack workspace to link.'
        }
      />
      {demoWorkspaceOption}

      {/* One scroll region for the invites and the workspace choices: a long invite list scrolls with the choices
          instead of pushing them out of the capped card. The side padding leaves room for the focus rings. */}
      <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
        <PendingInvites className="mt-6" />

        {listQuery.isError ? (
          <div className="mt-6 space-y-3">
            <Callout variant="error" className="text-left">
              Could not load your RudderStack workspaces.
            </Callout>
            <Button variant="secondary" size="md" onClick={() => void listQuery.refetch()}>
              Try again
            </Button>
          </div>
        ) : !pickerReady ? (
          // Spinner also covers the empty-cache redirect window (hasTokens=false): the useEffect
          // above navigates away, and we keep spinning rather than flashing the picker.
          <GatewaySpinner mode="inline" label="Loading workspaces" />
        ) : siblingAccess ? (
          <SiblingWorkspacePicker reauth={reauth} onCreated={leaveAfterCreate} onJoined={leaveAfterJoin} />
        ) : (
          <>
            {linkable.length > 3 && (
              <Input
                size="lg"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search workspaces…"
                aria-label="Search workspaces"
                className="mt-6 mb-4"
              />
            )}

            <div
              ref={listRef}
              tabIndex={-1}
              className={cn(
                'text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                linkable.length <= 3 && 'mt-6',
              )}
            >
              {workspaces.length === 0 ? (
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Building2 />
                    </EmptyMedia>
                    <EmptyTitle>
                      {data?.hasHiddenWorkspaces === true ? 'No workspaces to link' : 'No workspaces yet'}
                    </EmptyTitle>
                    <EmptyDescription>
                      {data?.hasHiddenWorkspaces === true
                        ? 'Your RudderStack workspaces are disabled or deleted. Ask a RudderStack admin if you need one, then refresh.'
                        : "You're not a member of any RudderStack workspace yet. Create one at RudderStack, then refresh."}
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              ) : linkable.length === 0 ? (
                // Every RudderStack workspace the user belongs to is already in Lookout — nothing to
                // link or join here; they switch between them from the workspace selector instead.
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Building2 />
                    </EmptyMedia>
                    <EmptyTitle>All caught up</EmptyTitle>
                    <EmptyDescription>
                      {data?.hasHiddenWorkspaces === true
                        ? 'Every listed RudderStack workspace is already in Lookout. Disabled or deleted ones are not listed. Switch between them from the workspace selector.'
                        : 'All your RudderStack workspaces are already in Lookout. Switch between them from the workspace selector.'}
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              ) : filtered.length === 0 ? (
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <SearchX />
                    </EmptyMedia>
                    <EmptyTitle>No matches</EmptyTitle>
                    <EmptyDescription>No workspaces match “{search.trim()}”.</EmptyDescription>
                  </EmptyHeader>
                </Empty>
              ) : (
                <div className="space-y-5">
                  {/* Club the joinable rows so the "Join your team" option reads as one coherent group
                    (instead of Join rows scattered alphabetically among Link rows). */}
                  {joinable.length > 0 && (
                    <section>
                      <h2 className="px-1 text-xs font-bold tracking-wider text-muted-foreground uppercase">
                        Join your team
                      </h2>
                      <p className="mt-1 mb-2 px-1 text-xs text-muted-foreground">
                        Joining keeps everyone's chats and dashboards together.
                      </p>
                      <ul className="space-y-2.5">{joinable.map(renderJoinCard)}</ul>
                    </section>
                  )}
                  {toLink.length > 0 && (
                    <section>
                      {joinable.length > 0 && (
                        <h2 className="mb-2 px-1 text-xs font-bold tracking-wider text-muted-foreground uppercase">
                          Link a new workspace
                        </h2>
                      )}
                      <ul className="space-y-2.5">{toLink.map(renderCreateCard)}</ul>
                    </section>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {pickerReady && pickerFooter}
    </GatewayShell>
  );
}
